(() => {
  window.WTCTheme.register('[data-flavor-theater]', (root) => {
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const stage = root.querySelector('.wtc-flavor-stage');
    const scene = root.querySelector('.wtc-flavor-scene');
    const drinks = [...root.querySelectorAll('[data-flavor-drink]')];
    const buttons = [...root.querySelectorAll('[data-flavor-select]')];
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const motionEnabled = root.dataset.motionEnabled !== 'false';
    const motionDisabled = () => reduced.matches || !motionEnabled;
    let selected = 0;
    let animations = [];
    let motionRun = 0;
    let pointerFrame = 0;
    let touch = null;
    let ready = false;
    let visible = false;
    let entrancePlayed = false;
    let userInteracted = false;

    function resetPointer() {
      cancelAnimationFrame(pointerFrame);
      pointerFrame = 0;
      scene.style.setProperty('--ft-mx', '0px');
      scene.style.setProperty('--ft-my', '0px');
    }

    function stopEntrance() {
      motionRun++;
      animations.forEach((animation) => animation.cancel());
      animations = [];
      delete root.dataset.entering;
    }

    function selectFlavor(index) {
      if (!drinks[index]) return;
      userInteracted = true;
      stopEntrance();
      resetPointer();
      selected = index;
      const flavor = drinks[index].dataset;
      for (const property of ['scene', 'ink', 'glow']) root.style.setProperty(`--ft-${property}`, flavor[property]);
      root.querySelector('.wtc-flavor-name').textContent = flavor.name;
      root.querySelector('.wtc-flavor-kicker').textContent = flavor.kicker;
      buttons.forEach((button, position) => button.setAttribute('aria-pressed', String(position === index)));
      drinks.forEach((drink, position) => { drink.dataset.position = String((position - selected + drinks.length) % drinks.length); });
    }

    function playEntrance() {
      if (motionDisabled() || !drinks.every((drink) => typeof drink.animate === 'function')) return;
      stopEntrance();
      resetPointer();
      entrancePlayed = true;
      root.dataset.entering = '';
      const run = motionRun;
      animations = drinks.map((drink) => {
        const rank = Number(drink.dataset.position);
        const style = getComputedStyle(drink);
        const transform = style.transform;
        const opacity = style.opacity;
        return drink.animate([
          { transform: `${transform} translateY(320px) rotate(-40deg) scale(.45)`, opacity: 0 },
          { transform: `${transform} translateY(-12px) rotate(2deg) scale(1.015)`, opacity, offset: .8 },
          { transform, opacity },
        ], { duration: 1250 + rank * 100, delay: rank * 90, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' });
      });
      Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
        if (!controller.signal.aborted && run === motionRun) stopEntrance();
      });
    }

    function maybeEnter() {
      if (ready && visible && !entrancePlayed && !userInteracted && !document.hidden) playEntrance();
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => selectFlavor(index), options);
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 :
          (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next].focus();
        selectFlavor(next);
      }, options);
    });

    stage.addEventListener('pointermove', (event) => {
      if (motionDisabled() || !finePointer.matches || event.pointerType === 'touch' || pointerFrame) return;
      pointerFrame = requestAnimationFrame(() => {
        const bounds = stage.getBoundingClientRect();
        scene.style.setProperty('--ft-mx', `${((event.clientX - bounds.left) / bounds.width - .5) * 24}px`);
        scene.style.setProperty('--ft-my', `${((event.clientY - bounds.top) / bounds.height - .5) * 14}px`);
        pointerFrame = 0;
      });
    }, options);
    stage.addEventListener('pointerleave', resetPointer, options);
    scene.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType !== 'touch' && (root.clientWidth > 600 || event.button !== 0))) return;
      touch = { x: event.clientX, y: event.clientY };
      scene.setPointerCapture(event.pointerId);
    }, options);
    scene.addEventListener('pointerup', (event) => {
      if (!touch) return;
      const dx = event.clientX - touch.x;
      const dy = event.clientY - touch.y;
      touch = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      selectFlavor((selected + (dx < 0 ? 1 : -1) + drinks.length) % drinks.length);
    }, options);
    for (const type of ['pointercancel', 'lostpointercapture']) scene.addEventListener(type, () => { touch = null; }, options);

    function applyMotionPreference() {
      root.classList.toggle('is-motion-disabled', motionDisabled());
      stopEntrance();
      resetPointer();
    }
    reduced.addEventListener('change', applyMotionPreference, options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { stopEntrance(); resetPointer(); }
      else maybeEnter();
    }, options);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) maybeEnter();
      else { stopEntrance(); resetPointer(); }
    }, { threshold: .15 }) : null;
    if (observer) observer.observe(stage);
    else visible = true;

    root.querySelector('.wtc-flavor-choices').hidden = false;
    applyMotionPreference();
    Promise.allSettled([document.fonts.ready, ...[...scene.querySelectorAll('img')].map((image) => image.decode())]).then(() => {
      if (controller.signal.aborted) return;
      ready = true;
      maybeEnter();
    });

    return () => {
      controller.abort();
      observer?.disconnect();
      stopEntrance();
      resetPointer();
    };
  });
})();
